import * as React from "react";
import { useCallback, useContext, useState } from "react";
import styled, { css } from "styled-components";
import { Formik, Form, Field } from "formik";
import Button from "components/bootstrap/Button";
import Icon from "components/common/Icon";
import Pluralize from "components/common/Pluralize";
import IconButton from "components/common/IconButton";
import Badge from "components/bootstrap/Badge";
import { Input } from "components/bootstrap";
import Spinner from "components/common/Spinner";
import useWidgetSummarySuggestedQuestions from "ai/widget-summary/useWidgetSummarySuggestedQuestions";
import useAssistantSidebar from "./useAssistantSidebar";
import { AssistantToolCallsContext } from "./AssistantSidebarContext";
import AssistantAttachment from "./AssistantAttachment";
import SummarySubject from "./SummarySubject";
import ToolCallList from "./ToolCallList";
import ChatBubble from "./ChatBubble";
import TimestampSeparator from "./TimestampSeparator";
const WidgetSummaryPromptContainer = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings.sm};
    padding: ${theme.spacings.sm} 0;
  `
);
const SuggestedQuestions = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacings.xs};
  `
);
const SuggestedQuestion = styled(Badge)(
  ({ theme }) => css`
    height: auto;
    padding-top: ${theme.spacings.xxs};
    padding-bottom: ${theme.spacings.xxs};
    text-align: left;

    .mantine-Badge-label {
      overflow: visible;
      white-space: normal;
    }
  `
);
const SubmittedQuestionContainer = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings.sm};
  `
);
const QuestionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
`;
const RetryContainer = styled.div(
  ({ theme }) => css`
    display: flex;
    justify-content: space-between;
    margin-top: ${theme.spacings.xs};
  `
);
const HelpContainer = styled.div(
  ({ theme }) => css`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: ${theme.spacings.sm};
  `
);
const ErrorAlert = styled.div(
  ({ theme }) => css`
    color: ${theme.colors.alerts.danger};
    font-size: ${theme.fonts.size.small};
    display: inline-flex;
    gap: ${theme.spacings.xs};
  `
);
const FormHelpComponent = ({ disabled }) => /* @__PURE__ */ React.createElement(HelpContainer, null, /* @__PURE__ */ React.createElement("span", null, "Enter to send - Shift+Enter for new line"), /* @__PURE__ */ React.createElement(Button, { type: "submit", bsStyle: "primary", disabled, title: "Submit question", "aria-label": "Submit question" }, /* @__PURE__ */ React.createElement(Icon, { name: "arrow_upward" })));
const WidgetSummaryQuestionPrompt = ({ request }) => {
  const { generate, isGenerating, isError } = useAssistantSidebar();
  const toolCalls = useContext(AssistantToolCallsContext);
  const suggestedQuestions = useWidgetSummarySuggestedQuestions(request);
  const [submittedRequest, setSubmittedRequest] = useState(null);
  const [submittedAt, setSubmittedAt] = useState(null);
  const submitQuestion = useCallback(
    (nextQuestion) => {
      const trimmedQuestion = nextQuestion.trim();
      if (trimmedQuestion) {
        const nextRequest = { ...request, question: trimmedQuestion };
        setSubmittedRequest(nextRequest);
        setSubmittedAt(/* @__PURE__ */ new Date());
        return generate(nextRequest).catch(() => {
        });
      }
      return Promise.resolve();
    },
    [generate, request]
  );
  const onSubmit = useCallback(({ question }) => submitQuestion(question), [submitQuestion]);
  const retry = useCallback(() => generate(submittedRequest), [generate, submittedRequest]);
  if (submittedRequest) {
    return /* @__PURE__ */ React.createElement(SubmittedQuestionContainer, null, /* @__PURE__ */ React.createElement(TimestampSeparator, { timestamp: submittedAt }), /* @__PURE__ */ React.createElement(QuestionContainer, null, /* @__PURE__ */ React.createElement(ChatBubble, null, /* @__PURE__ */ React.createElement(AssistantAttachment, { request: submittedRequest, size: "compact" }), /* @__PURE__ */ React.createElement("span", null, submittedRequest.question)), isError && /* @__PURE__ */ React.createElement(RetryContainer, null, /* @__PURE__ */ React.createElement(ErrorAlert, null, /* @__PURE__ */ React.createElement(Icon, { name: "warning" }), " Error occurred while generating"), /* @__PURE__ */ React.createElement(
      IconButton,
      {
        name: "refresh",
        title: "Retry question",
        "aria-label": "Retry question",
        onClick: () => {
          void retry();
        }
      }
    ))), isGenerating && !isError && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ToolCallList, { calls: toolCalls }), /* @__PURE__ */ React.createElement(Spinner, { text: "Generating response..." })));
  }
  return /* @__PURE__ */ React.createElement(WidgetSummaryPromptContainer, null, /* @__PURE__ */ React.createElement("h3", null, "What would you like to know?"), /* @__PURE__ */ React.createElement("span", null, request.payload.selection ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(SummarySubject, { request }), " ", /* @__PURE__ */ React.createElement(Pluralize, { value: request.payload.selection.messages.length, singular: "is", plural: "are" }), " attached.") : /* @__PURE__ */ React.createElement(React.Fragment, null, "The ", /* @__PURE__ */ React.createElement("strong", null, request.payload.name), " data from your current search is attached."), " ", "Ask anything about it, or pick a starting point:"), /* @__PURE__ */ React.createElement(AssistantAttachment, { request }), /* @__PURE__ */ React.createElement(Formik, { initialValues: { question: "" }, onSubmit }, ({ isSubmitting, submitForm, values }) => /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(SuggestedQuestions, null, suggestedQuestions.map((suggestedQuestion) => /* @__PURE__ */ React.createElement(
    SuggestedQuestion,
    {
      key: suggestedQuestion,
      onClick: () => {
        void submitQuestion(suggestedQuestion);
      },
      color: "gray",
      size: "lg"
    },
    suggestedQuestion
  ))), /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(Field, { name: "question" }, ({ field: { name, value, onChange } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "assistant-widget-summary-question",
      "aria-label": `Ask a question about ${request.payload.name}`,
      name,
      type: "textarea",
      rows: 4,
      autoFocus: true,
      value,
      onChange,
      onKeyDown: (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          void submitForm();
        }
      },
      placeholder: `Ask a question about ${request.payload.name}...`,
      help: /* @__PURE__ */ React.createElement(FormHelpComponent, { disabled: !values.question.trim() || isSubmitting })
    }
  ))))));
};
export default WidgetSummaryQuestionPrompt;
